/**
 * Списки в тексте задания (решение владельца 13.10.2026, п. 4): маркированный и
 * нумерованный, вложенность Tab / Shift+Tab, стиль маркера и нумерации.
 *
 * Хранение — тот же HTML, что и раньше (`<ul>`, `<ol>`, `<li>`), старые задания не
 * меняются. Стиль — атрибут `data-list`, а не HTML-атрибут `type`: значения `type`
 * браузер сравнивает БЕЗ учёта регистра, и CSS не отличил бы «a» от «A», «i» от «I».
 * DOMPurify `data-*` пропускает. Отрисовка — класс `rt` в index.css.
 *
 * ⚠️ Приложение 1.1.1 (старый CSS, preflight Tailwind) покажет списки строками без
 * маркеров и отступа — текст цел, номера пропадут. Обойти нельзя: CSS зашит в сборку.
 */

export const UL_STYLES = ['disc', 'circle', 'square'] as const;
export const OL_STYLES = ['decimal', 'lower-alpha', 'upper-alpha', 'lower-roman', 'upper-roman'] as const;
export type ListStyle = (typeof UL_STYLES)[number] | (typeof OL_STYLES)[number];

const isList = (n: Node | null): n is HTMLElement => !!n && (n.nodeName === 'UL' || n.nodeName === 'OL');

/**
 * Вложенный список — внутрь пункта. Chrome по Tab (`execCommand('indent')`) кладёт
 * `<ul>` прямо в `<ul>`: браузер такое рисует, но это не HTML, и у ученика (и в
 * старом WebView) отступ и маркер такого списка расходятся с редактором. Переносим
 * его в конец предыдущего `<li>`; если пункта перед ним нет — заворачиваем в новый.
 */
export function normalizeListNesting(html: string): string {
  if (!html || !/<(ul|ol)[\s>]/i.test(html)) return html;
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  let moved = true;
  // Повторяем, пока есть что двигать: перенос одного уровня открывает следующий.
  while (moved) {
    moved = false;
    for (const list of Array.from(doc.body.querySelectorAll('ul, ol'))) {
      const parent = list.parentNode;
      if (!isList(parent)) continue;
      let prev = list.previousSibling;
      while (prev && prev.nodeType === Node.TEXT_NODE && !(prev.textContent ?? '').trim()) prev = prev.previousSibling;
      if (prev && prev.nodeName === 'LI') {
        prev.appendChild(list);
      } else {
        const li = doc.createElement('li');
        parent.insertBefore(li, list);
        li.appendChild(list);
      }
      moved = true;
    }
  }
  return doc.body.innerHTML;
}

/** Список, внутри которого сейчас курсор (ближайший). */
export function listAtSelection(root: HTMLElement): HTMLElement | null {
  const sel = typeof window !== 'undefined' ? window.getSelection() : null;
  let n: Node | null = sel?.anchorNode ?? null;
  while (n && n !== root) {
    if (isList(n)) return n;
    n = n.parentNode;
  }
  return null;
}

/** Курсор внутри пункта списка — тогда Tab вкладывает, а не уводит фокус. */
export function selectionInListItem(root: HTMLElement): boolean {
  const sel = typeof window !== 'undefined' ? window.getSelection() : null;
  let n: Node | null = sel?.anchorNode ?? null;
  while (n && n !== root) {
    if (n.nodeName === 'LI') return true;
    n = n.parentNode;
  }
  return false;
}

/** Стиль маркера или нумерации ближайшего списка; стиль чужого вида не ставится. */
export function setListStyle(list: HTMLElement, style: ListStyle): boolean {
  const allowed: readonly string[] = list.nodeName === 'UL' ? UL_STYLES : OL_STYLES;
  if (!allowed.includes(style)) return false;
  list.setAttribute('data-list', style);
  return true;
}
